import { useState, useEffect, useCallback, useRef } from "react";
import type { ChatMessageResponseDto } from "@semo/api-contracts";
import type { PendingChatMessage, PendingQuoteMessage } from "../types/message";

/**
 * Options for useUnreadMessages hook
 */
export interface UseUnreadMessagesOptions {
  messages: (
    | PendingChatMessage
    | PendingQuoteMessage
    | ChatMessageResponseDto
  )[];
  currentUserId: string;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  /**
   * Distance from bottom (in px) to consider "at bottom"
   * @default 100
   */
  scrollThreshold?: number;
}

/**
 * UI-layer hook for managing unread message count and scroll behavior
 *
 * Features:
 * - Tracks scroll position (isAtBottom)
 * - Counts unread messages when scrolled up
 * - Auto-scrolls for own messages
 * - Conditional auto-scroll for others' messages (only when at bottom)
 * - Provides smooth scroll-to-bottom function
 *
 * @example
 * ```tsx
 * const { messages, sendMessage } = useChatMessages({ ... });
 * const { unreadCount, handleScroll, scrollToBottom } = useUnreadMessages({
 *   messages,
 *   currentUserId: user.id,
 *   messagesEndRef
 * });
 *
 * return (
 *   <div onScroll={handleScroll}>
 *     {messages.map(...)}
 *     {unreadCount > 0 && (
 *       <button onClick={scrollToBottom}>
 *         새 메시지 {unreadCount}개 ↓
 *       </button>
 *     )}
 *   </div>
 * );
 * ```
 */
export function useUnreadMessages({
  messages,
  currentUserId,
  messagesEndRef,
  scrollThreshold = 100,
}: UseUnreadMessagesOptions) {
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  // Track previous message count to detect new messages
  const prevMessageCountRef = useRef(messages.length);
  // Track last message ID to distinguish between "new message arrived" vs "history loaded"
  const lastMessageIdRef = useRef<string | null>(null);

  /**
   * Scroll position detection handler
   * Determines if user is near the bottom of the chat
   */
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const isNearBottom = distanceFromBottom < scrollThreshold;

      setIsAtBottom(isNearBottom);

      // Clear unread count when scrolling to bottom
      if (isNearBottom) {
        setUnreadCount(0);
      }
    },
    [scrollThreshold],
  );

  /**
   * Smooth scroll to bottom of chat
   */
  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = "smooth") => {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior });
      }, 0);
    },
    [messagesEndRef],
  );

  /**
   * Handle new message arrival
   * - Own message → Always auto-scroll
   * - Other's message + at bottom → Auto-scroll
   * - Other's message + scrolled up → Increment unread count
   */
  useEffect(() => {
    if (messages.length === 0) {
      // ✅ Even if no messages, being at the "bottom" of an empty container is true.
      // This ensures that when the first batch of messages load, we might trigger a read check.
      setIsAtBottom(true);
      lastMessageIdRef.current = null;
      return;
    }

    const lastMessage = messages[messages.length - 1];
    if (!lastMessage) return;

    // Detect new messages
    const isCountIncreased = messages.length > prevMessageCountRef.current;
    const isLastMessageChanged = lastMessage.id !== lastMessageIdRef.current;

    prevMessageCountRef.current = messages.length;
    lastMessageIdRef.current = lastMessage.id;

    // Only trigger auto-scroll logic if a NEW message arrived at the bottom.
    // If count increased but last message is same, it means history was loaded at the top.
    if (!isCountIncreased || !isLastMessageChanged) {
      return;
    }

    const isOwnMessage = lastMessage.senderId === currentUserId;

    // Own message → Always scroll to bottom
    if (isOwnMessage) {
      scrollToBottom("auto");
      return;
    }

    // Other's message
    if (isAtBottom) {
      // At bottom → Auto-scroll smoothly
      scrollToBottom("smooth");
    } else {
      // Scrolled up → Increment unread count
      setUnreadCount((prev) => prev + 1);
    }
  }, [messages, currentUserId, isAtBottom, scrollToBottom]);

  /**
   * Clear unread count manually
   */
  const clearUnread = useCallback(() => {
    setUnreadCount(0);
  }, []);

  return {
    /** Whether user is at the bottom of the chat */
    isAtBottom,
    /** Number of unread messages (incremented when scrolled up) */
    unreadCount,
    /** Scroll event handler to attach to scrollable container */
    handleScroll,
    /** Function to scroll to bottom of chat */
    scrollToBottom,
    /** Function to manually clear unread count */
    clearUnread,
  };
}
